/**
 * Add Items Modal
 * Reusable modal for selecting products to add to a booking
 * Adapted from NewBookingFlow product selection
 */

import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Package, Loader2, Plus, Truck, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { checkAvailability, type AvailableProduct } from '../services/bookings.service';
import { getProducts } from '../services/products.service';
import { isActiveProduct, isHiddenProduct } from '../utils/productVisibility';
import HiddenProductBadge from './HiddenProductBadge';
import { calculatePrice, pricingRuleColumns, type ItemPricingRuleColumns } from '../services/pricing.service';
import { getLinkedAddonsForProducts } from '../services/productAddons.service';
const RENTABLE_PRODUCT_TYPES = ['bike', 'sup', 'kayak', 'other'];

export type AddItemsModalKind = 'products' | 'addons';

interface AddItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  merchantId: string;
  startDate: string;
  endDate: string;
  bookingId?: string;
  existingItems: Array<{
    inventory_item_id: string | null;
    product_type_id?: string | null;
    quantity?: number | null;
    is_bundle_parent?: boolean | null;
  }>;
  currencySymbol?: string;
  kind?: AddItemsModalKind;
  onAddItem: (item: {
    product_type_id: string;
    item_name: string;
    size: string | null;
    quantity: number;
    unit_price: number;
    inventory_item_id: string | null;
    inventory_type: 'individual' | 'pool' | 'unlimited';
    duration_hours: number;
    total_price: number;
    /** The rule the item was priced under; the bundle's rule for a bundle. */
    ruleColumns: ItemPricingRuleColumns;
    isBundle?: boolean;
    bundleComponents?: Array<{
      product_type_id: string;
      product_name: string;
      quantity: number;
      size: string | null;
      inventoryItemIds: string[];
      inventoryType: 'individual' | 'pool' | 'unlimited';
    }>;
  }) => void;
}

interface GroupedProducts {
  [productName: string]: {
    name: string;
    picture_url: string | null;
    hidden: boolean;
    sizes: {
      [size: string]: AvailableProduct[];
    };
  };
}

export default function AddItemsModal({
  isOpen,
  onClose,
  locationId,
  merchantId,
  startDate,
  endDate,
  bookingId,
  existingItems,
  currencySymbol = '$',
  kind = 'products',
  onAddItem
}: AddItemsModalProps) {
  const { t } = useTranslation();
  const isAddonsMode = kind === 'addons';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableProducts, setAvailableProducts] = useState<AvailableProduct[]>([]);
  const [linkedAddonMeta, setLinkedAddonMeta] = useState<Map<string, { recommended: boolean }>>(new Map());
  const [productTypeDetails, setProductTypeDetails] = useState<Map<string, {
    product_type: string;
    pool_quantity: number | null;
    inventory_type: string | null;
    location_id: string | null;
    location_name: string | null;
    active: boolean | null;
    hidden: boolean | null;
  }>>(new Map());

  useEffect(() => {
    if (isOpen) {
      loadAvailability();
    }
    // Reset per-open state so reopening in a different mode doesn't show stale data.
    if (!isOpen) {
      setError(null);
    }
  }, [isOpen, startDate, endDate, locationId, kind]);

  async function loadAvailability() {
    setLoading(true);
    setError(null);

    // Get excluded inventory item IDs (items already in the booking)
    const excludedIds = existingItems
      .filter(item => item.inventory_item_id)
      .map(item => item.inventory_item_id!);

    // Check availability — pass bookingId to exclude the current booking from
    // overlap detection and skip pre-booking/cutoff time gates in edit flow
    const availabilityResult = await checkAvailability({
      locationId,
      startDate,
      endDate,
      excludedInventoryItemIds: excludedIds,
      excludedBookingId: bookingId,
    });

    if (availabilityResult.error) {
      setError(availabilityResult.error);
      setLoading(false);
      return;
    }

    // Fetch product types for enrichment
    const productTypesResult = await getProducts(
      { merchantId, locationIds: null, isPlatformAdmin: false },
      {}
    );

    if (productTypesResult.error) {
      setError(productTypesResult.error);
      setLoading(false);
      return;
    }

    if (availabilityResult.data && productTypesResult.data) {
      // Create product type map
      const productTypeMap = new Map<string, { product_type: string; pool_quantity: number | null; inventory_type: string | null; location_id: string | null; location_name: string | null; active: boolean | null; hidden: boolean | null; }>(
        productTypesResult.data.map((pt: any) => [pt.id, {
          product_type: pt.product_type,
          pool_quantity: pt.pool_quantity,
          inventory_type: pt.inventory_type,
          location_id: pt.location_id,
          location_name: (pt as any).locations?.location_name || null,
          active: pt.active,
          hidden: pt.hidden,
        }])
      );

      setProductTypeDetails(productTypeMap);

      // Enrich availability results
      const enrichedProducts = availabilityResult.data.map(product => ({
        ...product,
        product_type: product.product_type && product.product_type !== 'unknown'
          ? product.product_type
          : (productTypeMap.get(product.product_type_id)?.product_type || product.product_type || 'unknown')
      }));

      setAvailableProducts(enrichedProducts);

      // In addon mode, resolve which of the available addons are linked to
      // any rentable already in this booking so we can surface them first.
      if (isAddonsMode) {
        const parentProductTypeIds = Array.from(new Set(
          existingItems.map(i => i.product_type_id).filter((id): id is string => !!id)
        ));
        const { data: linkedMap } = await getLinkedAddonsForProducts(parentProductTypeIds);
        setLinkedAddonMeta(linkedMap || new Map());
      } else {
        setLinkedAddonMeta(new Map());
      }
    }

    setLoading(false);
  }

  // ── Single source of truth for booking-aware availability ──
  //
  // checkAvailability runs with excludedBookingId, so the RPC's pool_quantity and
  // individual rows ADD this booking's own usage back in. We subtract what the
  // booking already holds (its existing items, including flattened bundle component
  // rows) so display, guards and item assignment all agree on what's actually free.
  type CartComponent = { product_type_id: string; quantity: number; size?: string | null };
  const cartAvailability = useMemo(() => {
    const consumedItemIds = new Set<string>();
    const consumedPoolByType = new Map<string, number>();
    for (const it of existingItems) {
      if (it.is_bundle_parent) continue; // bundle parents hold no real inventory
      if (it.inventory_item_id) consumedItemIds.add(it.inventory_item_id);
      else if (it.product_type_id) {
        // No inventory_item_id ⇒ pool/unlimited aggregate row. (Unlimited entries
        // are harmless: remainingPool is only consulted for pool types.)
        consumedPoolByType.set(it.product_type_id, (consumedPoolByType.get(it.product_type_id) || 0) + (it.quantity || 1));
      }
    }

    const poolCapacity = (typeId: string): number => {
      const rows = availableProducts.filter(p => p.product_type_id === typeId && p.inventory_type === 'pool');
      return rows.length ? Math.max(0, ...rows.map(r => r.pool_quantity || 0)) : 0;
    };
    const remainingPool = (typeId: string): number =>
      Math.max(0, poolCapacity(typeId) - (consumedPoolByType.get(typeId) || 0));

    const individualItems = (typeId: string, size: string | null | undefined, excludeConsumed: boolean) =>
      availableProducts.filter(p =>
        p.product_type_id === typeId &&
        p.inventory_type === 'individual' &&
        !!p.item_id &&
        (!size || p.size === size) &&
        (!excludeConsumed || !consumedItemIds.has(p.item_id!))
      );
    const remainingIndividualItems = (typeId: string, size?: string | null) =>
      individualItems(typeId, size, true);

    const componentRemaining = (comp: CartComponent, includeCart: boolean): number => {
      const rows = availableProducts.filter(p => p.product_type_id === comp.product_type_id);
      if (rows.length === 0) return 0;
      const invType = rows[0].inventory_type;
      if (invType === 'unlimited') return Infinity;
      if (invType === 'pool') return includeCart ? remainingPool(comp.product_type_id) : poolCapacity(comp.product_type_id);
      // Each bundle consumes one item of ANY size (when size is unspecified), so
      // the count is bounded by the TOTAL remaining items, not the largest size
      // group. individualItems already filters to comp.size when set.
      const items = individualItems(comp.product_type_id, comp.size || undefined, includeCart);
      return items.length;
    };

    const bundleCount = (bundle: { bundle_components: unknown }, includeCart: boolean): number => {
      const comps = bundle.bundle_components as CartComponent[] | null;
      if (!Array.isArray(comps) || comps.length === 0) return 0;
      let min = Infinity;
      for (const comp of comps) {
        min = Math.min(min, Math.floor(componentRemaining(comp, includeCart) / (comp.quantity || 1)));
        if (min === 0) break;
      }
      return min;
    };

    return {
      consumedItemIds,
      consumedPool: (typeId: string) => consumedPoolByType.get(typeId) || 0,
      remainingPool,
      remainingIndividualItems,
      bundleRemaining: (bundle: { bundle_components: unknown }) => bundleCount(bundle, true),
      bundleRemainingBase: (bundle: { bundle_components: unknown }) => bundleCount(bundle, false),
    };
  }, [availableProducts, existingItems]);

  // Hide bundles that are structurally unfulfillable (components can't make even
  // one bundle, ignoring the cart). Cart exhaustion disables the button instead.
  const unavailableBundleIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of availableProducts.filter(p => p.is_bundle && p.bundle_components)) {
      if (cartAvailability.bundleRemainingBase(p) < 1) ids.add(p.product_type_id);
    }
    return ids;
  }, [availableProducts, cartAvailability]);

  // Group products by name and size
  const groupedProducts: GroupedProducts = availableProducts
    .filter(product => {
      if (!product.product_type) return false;
      if (!isActiveProduct(productTypeDetails.get(product.product_type_id))) return false;
      if (isAddonsMode) {
        // Addons mode: only addon-typed products, no bundles.
        return product.product_type === 'addon' && !product.is_bundle;
      }
      // Products mode (default): rentables + bundles, no addons.
      if (product.product_type === 'addon') return false;
      if (product.is_bundle && unavailableBundleIds.has(product.product_type_id)) return false;
      return RENTABLE_PRODUCT_TYPES.includes(product.product_type) || product.product_type === 'unknown';
    })
    .reduce((acc, product) => {
      const key = product.product_name;
      if (!acc[key]) {
        acc[key] = {
          name: product.product_name,
          picture_url: product.picture_url,
          hidden: true,
          sizes: {},
        };
      }
      // Products sharing a name are grouped; badge the card only if all of them are hidden
      if (!isHiddenProduct(productTypeDetails.get(product.product_type_id))) acc[key].hidden = false;
      const sizeKey = product.size || t('addItems.oneSize');
      if (!acc[key].sizes[sizeKey]) {
        acc[key].sizes[sizeKey] = [];
      }
      acc[key].sizes[sizeKey].push(product);
      return acc;
    }, {} as GroupedProducts);

  // In addon mode, rank groups: recommended-linked → linked → other.
  // Within each rank, keep alphabetical order.
  const groupedEntries = Object.entries(groupedProducts);
  if (isAddonsMode) {
    const rank = (productTypeId: string | undefined) => {
      if (!productTypeId) return 2;
      const meta = linkedAddonMeta.get(productTypeId);
      if (!meta) return 2;
      return meta.recommended ? 0 : 1;
    };
    groupedEntries.sort(([nameA, groupA], [nameB, groupB]) => {
      const rA = rank(Object.values(groupA.sizes)[0]?.[0]?.product_type_id);
      const rB = rank(Object.values(groupB.sizes)[0]?.[0]?.product_type_id);
      if (rA !== rB) return rA - rB;
      return nameA.localeCompare(nameB);
    });
  }

  const handleAddProduct = async (product: AvailableProduct) => {
    setError(null);

    // Calculate duration in hours
    const start = new Date(startDate);
    const end = new Date(endDate);
    const elapsedHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    const durationHours = Math.round(elapsedHours);

    // Calculate price
    const { data: pricingData, error: pricingError } = await calculatePrice(
      product.product_type_id,
      locationId,
      Math.floor(durationHours / 24), // days
      durationHours % 24, // hours
      product.is_bundle ? null : product.size,
      'admin',
      elapsedHours
    );

    if (pricingError || !pricingData) {
      setError(pricingError || t('addItems.unableToCalculatePrice'));
      return;
    }

    // ── Bundle handling ──
    if (product.is_bundle && product.bundle_components) {
      const components = product.bundle_components as Array<{ product_type_id: string; quantity: number; size?: string | null }>;
      const excludedIds = [...cartAvailability.consumedItemIds];
      // Pool inventory reserved while resolving THIS bundle, keyed by component
      // product_type_id, so two pool components of the same type don't both claim
      // the same units (on top of what the booking already reserves).
      const poolReservedThisBundle = new Map<string, number>();
      const resolvedComponents: Array<{
        product_type_id: string; product_name: string; quantity: number;
        size: string | null; inventoryItemIds: string[]; inventoryType: 'individual' | 'pool' | 'unlimited';
      }> = [];

      for (const comp of components) {
        const needed = comp.quantity || 1;
        const { data: compAvail, error: compError } = await checkAvailability({
          locationId, startDate, endDate,
          productTypeId: comp.product_type_id,
          size: comp.size || undefined,
          excludedInventoryItemIds: excludedIds.length > 0 ? excludedIds : undefined,
          excludedBookingId: bookingId,
        });

        if (compError || !compAvail) {
          setError(`Bundle component unavailable: ${compError || 'No availability data'}`);
          return;
        }

        const compInvType = compAvail[0]?.inventory_type as 'individual' | 'pool' | 'unlimited' || 'individual';
        const compName = compAvail[0]?.product_name || 'Component';

        if (compInvType === 'individual') {
          const items = comp.size ? compAvail.filter(i => i.size === comp.size) : compAvail;
          if (items.length < needed) {
            setError(`Not enough ${compName} available (need ${needed}, have ${items.length})`);
            return;
          }
          const ids = items.slice(0, needed).map(i => i.item_id!);
          excludedIds.push(...ids);
          resolvedComponents.push({ product_type_id: comp.product_type_id, product_name: compName, quantity: needed, size: comp.size || items[0]?.size || null, inventoryItemIds: ids, inventoryType: 'individual' });
        } else if (compInvType === 'pool') {
          const poolQty = compAvail[0]?.pool_quantity || 0;
          const alreadyReserved =
            cartAvailability.consumedPool(comp.product_type_id) +
            (poolReservedThisBundle.get(comp.product_type_id) || 0);
          const remaining = poolQty - alreadyReserved;
          if (remaining < needed) {
            setError(`Not enough ${compName} available (need ${needed}, have ${Math.max(0, remaining)})`);
            return;
          }
          poolReservedThisBundle.set(
            comp.product_type_id,
            (poolReservedThisBundle.get(comp.product_type_id) || 0) + needed
          );
          resolvedComponents.push({ product_type_id: comp.product_type_id, product_name: compName, quantity: needed, size: comp.size || null, inventoryItemIds: [], inventoryType: 'pool' });
        } else {
          // unlimited — no quantity ceiling
          resolvedComponents.push({ product_type_id: comp.product_type_id, product_name: compName, quantity: needed, size: comp.size || null, inventoryItemIds: [], inventoryType: 'unlimited' });
        }
      }

      onAddItem({
        product_type_id: product.product_type_id,
        item_name: product.product_name,
        size: null,
        quantity: 1,
        unit_price: pricingData.price,
        inventory_item_id: null,
        inventory_type: product.inventory_type,
        duration_hours: durationHours,
        total_price: pricingData.price,
        ruleColumns: pricingRuleColumns(pricingData.rule),
        isBundle: true,
        bundleComponents: resolvedComponents,
      });
      onClose();
      return;
    }

    // ── Non-bundle handling ──
    // The RPC runs with excludedBookingId, so it re-includes this booking's own
    // items/pool usage. Pick from what's actually free per the cart-aware source.
    let inventoryItemId = product.item_id;
    if (product.inventory_type === 'individual') {
      const remaining = cartAvailability.remainingIndividualItems(product.product_type_id, product.size);
      if (remaining.length === 0) {
        setError(`Not enough ${product.product_name} available`);
        return;
      }
      inventoryItemId = remaining[0].item_id;
    } else if (product.inventory_type === 'pool') {
      if (cartAvailability.remainingPool(product.product_type_id) <= 0) {
        setError(`Not enough ${product.product_name} available`);
        return;
      }
    }

    const itemData = {
      product_type_id: product.product_type_id,
      item_name: product.product_name,
      size: product.size,
      quantity: 1,
      unit_price: pricingData.price,
      inventory_item_id: inventoryItemId,
      inventory_type: product.inventory_type,
      duration_hours: durationHours,
      total_price: pricingData.price,
      ruleColumns: pricingRuleColumns(pricingData.rule),
    };

    onAddItem(itemData);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 isolate"
      data-testid="booking-edit-add-items-modal-backdrop"
    >
      <div
        className="rounded-lg shadow-2xl max-w-4xl w-full max-h-85vh overflow-hidden flex flex-col mx-auto relative z-50"
        style={{ backgroundColor: '#f2f2f2' }}
        data-testid="booking-edit-add-items-modal"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-md sm:p-lg border-b border-primary bg-white flex-shrink-0">
          <div className="flex items-center gap-xs sm:gap-md">
            <Plus className="w-5 h-5 sm:w-6 sm:h-6 text-blue flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-lg sm:text-2xl font-bold text-primary">
                {isAddonsMode ? t('addItems.addonTitle') : t('addItems.title')}
              </h2>
              <p className="text-xs text-secondary" data-testid="booking-edit-add-items-modal-currency-hint">
                {t('addItems.currency')}: {currencySymbol}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="btn-icon flex-shrink-0"
            title={t('addItems.close')}
            data-testid="booking-edit-add-items-modal-close-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-md sm:p-lg bg-white" data-testid="booking-edit-add-items-modal-content">
          {error && (
            <div className="p-md bg-danger/10 border-l-4 border-danger rounded-lg mb-lg">
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-2xl">
              <Loader2 className="w-8 h-8 text-blue animate-spin" />
              <span className="ml-md text-secondary">{t('addItems.checkingAvailability')}</span>
            </div>
          ) : groupedEntries.length === 0 ? (
            <div className="text-center py-2xl">
              <Package className="w-16 h-16 text-secondary mx-auto mb-md opacity-50" />
              <p className="text-secondary">
                {isAddonsMode ? t('addItems.noAddonsAvailable') : t('addItems.noProductsAvailable')}
              </p>
            </div>
          ) : (
            <div className="space-y-md">
              {/* Availability Legend */}
              <div className="flex flex-wrap items-center gap-xs sm:gap-md text-xs text-secondary bg-gray-100 p-sm rounded">
                <span className="font-medium">{t('addItems.availability')}:</span>
                <span className="inline-flex items-center gap-xs whitespace-nowrap">
                  <span className="w-3 h-3 rounded bg-green-200 border border-green-300"></span> {t('addItems.high')}
                </span>
                <span className="inline-flex items-center gap-xs whitespace-nowrap">
                  <span className="w-3 h-3 rounded bg-yellow-200 border border-yellow-300"></span> {t('addItems.medium')}
                </span>
                <span className="inline-flex items-center gap-xs whitespace-nowrap">
                  <span className="w-3 h-3 rounded bg-red-200 border border-red-300"></span> {t('addItems.low')}
                </span>
              </div>

              {/* Products Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-sm sm:gap-md">
                {groupedEntries.map(([productName, productGroup]) => {
                  const firstProductTypeId = Object.values(productGroup.sizes)[0]?.[0]?.product_type_id;
                  const linkMeta = isAddonsMode && firstProductTypeId ? linkedAddonMeta.get(firstProductTypeId) : undefined;
                  return (
                  <div key={productName} className="border border-border rounded-lg p-sm sm:p-md bg-white shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-sm sm:gap-md mb-sm">
                      {productGroup.picture_url ? (
                        <img
                          src={productGroup.picture_url}
                          alt={productGroup.name}
                          className="w-16 h-16 sm:w-20 sm:h-20 object-contain bg-white rounded-lg border border-border flex-shrink-0 p-1 sm:p-2"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-secondary/10 border border-border flex items-center justify-center text-secondary flex-shrink-0">
                          <Package className="w-6 h-6 sm:w-7 sm:h-7" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-primary text-sm sm:text-base mb-xs break-words flex items-center gap-xs flex-wrap">
                          {productGroup.name}
                          {productGroup.hidden && <HiddenProductBadge />}
                          {linkMeta?.recommended && (
                            <span className="inline-flex items-center gap-xs px-xs py-0.5 text-xxs rounded bg-yellow-100 text-yellow-800 border border-yellow-300">
                              <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                              {t('addItems.recommended')}
                            </span>
                          )}
                          {(() => {
                            const firstItem = Object.values(productGroup.sizes)[0]?.[0];
                            if (!firstItem) return null;
                            const ptDetails = productTypeDetails.get(firstItem.product_type_id);
                            if (ptDetails?.location_id && ptDetails.location_id !== locationId) {
                              return (
                                <span title={t('addItems.transferRequired', { location: ptDetails.location_name || t('addItems.anotherLocation') })}>
                                  <Truck className="w-4 h-4 text-danger flex-shrink-0" />
                                </span>
                              );
                            }
                            return null;
                          })()}
                        </h3>
                        <p className="text-xs text-secondary">
                          {t('addItems.availableInSizes', { count: Object.keys(productGroup.sizes).length })}
                        </p>
                      </div>
                    </div>

                    {/* Size Buttons */}
                    <div className="flex flex-wrap gap-sm">
                      {Object.entries(productGroup.sizes).map(([size, items]) => {
                        const firstItem = items[0];
                        const inventoryType = firstItem.inventory_type;

                        // Cart-aware remaining: bundles use MIN over components,
                        // standalone pool/individual subtract the booking's own usage.
                        const isUnlimited = firstItem.is_bundle
                          ? cartAvailability.bundleRemaining(firstItem) === Infinity
                          : inventoryType === 'unlimited';
                        const availableCount = isUnlimited
                          ? Infinity
                          : firstItem.is_bundle
                            ? cartAvailability.bundleRemaining(firstItem)
                            : inventoryType === 'pool'
                              ? cartAvailability.remainingPool(firstItem.product_type_id)
                              : cartAvailability.remainingIndividualItems(firstItem.product_type_id, firstItem.size).length;

                        const soldOut = !isUnlimited && availableCount <= 0;
                        const stockLevel = isUnlimited ? 'high' : (availableCount >= 5 ? 'high' : availableCount >= 3 ? 'medium' : 'low');
                        const badgeStyles = {
                          high: 'bg-green-100 hover:bg-green-200 border-green-300 text-green-800',
                          medium: 'bg-yellow-100 hover:bg-yellow-200 border-yellow-300 text-yellow-800',
                          low: 'bg-red-100 hover:bg-red-200 border-red-300 text-red-800'
                        };

                        return (
                          <button
                            key={size}
                            type="button"
                            disabled={soldOut}
                            onClick={() => handleAddProduct(items[0])}
                            className={`inline-flex items-center gap-xs px-md py-2 border rounded-full transition-all font-semibold text-sm ${soldOut ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed opacity-60' : `hover:shadow-md active:scale-95 ${badgeStyles[stockLevel]}`}`}
                          >
                            <span className="truncate max-w-[10rem]">{size}</span>
                            <span className="font-bold flex-shrink-0">({isUnlimited ? '\u221E' : availableCount})</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-primary p-md sm:p-lg flex items-center justify-end bg-white flex-shrink-0">
          <button
            onClick={onClose}
            className="btn btn-ghost"
            data-testid="booking-edit-add-items-modal-cancel-button"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
